import React from "react";
import { useTranslation } from "react-i18next";

import { Modal } from "components/Modal/Modal";
import TextInput from "components/inputs/TextInput";
import TextArea from "components/inputs/TextAreaInput";
import RadioInput from "components/inputs/RadioInput";
import SearchSelectInput from "components/inputs/SearchSelectInput";
import { PrimaryButton } from "components/Button/PrimaryButton";

enum AddMemberOption {
  existing = "existing",
  scratch = "scratch",
}

interface TemplateOption {
  crn: string;
  label: string;
}

interface Props {
  show: boolean;
  onClose: () => void;
  availableTemplates: TemplateOption[];
  onSelectExisting: (crn: string) => void;
  onCreateNew: (name: string, description: string) => void;
  isSubmitting?: boolean;
}

export const AddRiskTemplateToGroupModal: React.FC<Props> = ({
  show,
  onClose,
  availableTemplates,
  onSelectExisting,
  onCreateNew,
  isSubmitting,
}) => (
  <Modal
    show={show}
    onClose={onClose}
    width="1/2"
    content={
      <AddRiskTemplateToGroupForm
        availableTemplates={availableTemplates}
        onSelectExisting={onSelectExisting}
        onCreateNew={onCreateNew}
        onClose={onClose}
        isSubmitting={isSubmitting}
      />
    }
  />
);

interface FormProps {
  availableTemplates: TemplateOption[];
  onSelectExisting: (crn: string) => void;
  onCreateNew: (name: string, description: string) => void;
  onClose: () => void;
  isSubmitting?: boolean;
}

const AddRiskTemplateToGroupForm: React.FC<FormProps> = ({
  availableTemplates,
  onSelectExisting,
  onCreateNew,
  onClose,
  isSubmitting,
}) => {
  const { t } = useTranslation();
  const [selectedOption, setSelectedOption] = React.useState<AddMemberOption>(
    AddMemberOption.existing
  );
  const [selectedCrn, setSelectedCrn] = React.useState("");
  const [name, setName] = React.useState("");
  const [description, setDescription] = React.useState("");

  const handleSubmit = () => {
    if (selectedOption === AddMemberOption.existing && selectedCrn) {
      onSelectExisting(selectedCrn);
    } else if (selectedOption === AddMemberOption.scratch && name) {
      onCreateNew(name, description);
    }
  };

  const options = [
    {
      label: t("riskTemplate.groupings.add_member_option_existing"),
      value: AddMemberOption.existing,
    },
    {
      label: t("riskTemplate.groupings.add_member_option_scratch"),
      value: AddMemberOption.scratch,
    },
  ];

  const isDisabled =
    isSubmitting ||
    (selectedOption === AddMemberOption.existing && !selectedCrn) ||
    (selectedOption === AddMemberOption.scratch && (!name || !description));

  return (
    <div>
      <span className="text-xl">
        {t("riskTemplate.groupings.add_member_modal_headline")}
      </span>
      <div className="pt-4 pb-4">
        <RadioInput
          islast
          required
          id="addMemberOption"
          name="addMemberOption"
          label={t("riskTemplate.groupings.add_member_choice")}
          defaultValue={selectedOption}
          onBlur={(option) =>
            setSelectedOption(option.target.value as AddMemberOption)
          }
          options={options}
        />

        {selectedOption === AddMemberOption.existing && (
          <SearchSelectInput
            required
            id="existingTemplate"
            name="existingTemplate"
            label={t("riskTemplate.groupings.add_member_select_label")}
            defaultValue={
              availableTemplates.find((tpl) => tpl.crn === selectedCrn)?.label
            }
            onBlur={(option) => setSelectedCrn(option.target.value)}
            options={availableTemplates.map((template) => ({
              value: template.crn,
              label: template.label,
            }))}
          />
        )}

        {selectedOption === AddMemberOption.scratch && (
          <>
            <TextInput
              required
              uncontrolled
              id="newMemberName"
              name="newMemberName"
              label={t("riskTemplate.groupings.add_member_name_label")}
              defaultValue={name}
              // @ts-ignore
              onChange={(e) => setName(e.target.value)}
              placeholder={t(
                "riskTemplate.groupings.add_member_name_placeholder"
              )}
            />
            <TextArea
              required
              uncontrolled
              id="newMemberDescription"
              name="newMemberDescription"
              label={t("riskTemplate.groupings.add_member_description_label")}
              defaultValue={description}
              // @ts-ignore
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t(
                "riskTemplate.groupings.add_member_description_placeholder"
              )}
            />
          </>
        )}
      </div>
      <div className="text-right flex justify-end items-center">
        <a
          className="px-6 text-sm text-gray-500 hover:text-gray-700 cursor-pointer"
          onClick={onClose}
        >
          {t("risks.group.cancel")}
        </a>
        <PrimaryButton disabled={isDisabled} onClick={handleSubmit}>
          {isSubmitting
            ? t("riskTemplate.groupings.add_member_cta_loading")
            : t("riskTemplate.groupings.add_member_cta")}
        </PrimaryButton>
      </div>
    </div>
  );
};
